import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { queryDocuments } from '../../utility/firebaseHelpers';
import { where } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { downloadCSV } from '../../utility/csvUtils';
import {
  TrashIcon,
  DocumentArrowUpIcon,
  PaperClipIcon,
  PlusIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../modal/ConfirmationModal';

interface Expense {
  id: string;
  userId: string;
  listingId: string | null;
  amount: number;
  category: string;
  expenseDate: string; // ISO string from backend
  receiptUrl: string | null;
  createdAt: string;
}

interface ExpenseFormData {
  amount: number;
  category: string;
  dateAdded: string;
  listingId: string;
  receipt: FileList;
}

const EXPENSE_CATEGORIES = [
  'Repairs & Maintenance',
  'Management Fees',
  'Insurance',
  'Mortgage Interest',
  'Utilities',
  'Cleaning',
  'Advertising',
  'Legal & Professional Fees',
  'Other'
];

const ExpenseTracker: React.FC = () => {
  const { currentUser } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<ExpenseFormData>();

  const receiptFile = watch('receipt');

  useEffect(() => {
    if (receiptFile && receiptFile.length > 0) {
      const file = receiptFile[0];
      const previewUrl = URL.createObjectURL(file);
      setReceiptPreview(previewUrl);

      return () => {
        URL.revokeObjectURL(previewUrl);
      };
    } else {
      setReceiptPreview(null);
    }
  }, [receiptFile]);

  const fetchExpenses = useCallback(async () => {
    if (!currentUser?.uid) return;
    setIsLoading(true);
    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/getLandlordExpenses?userId=${currentUser.uid}`
      );
      const data = await response.json();
      if (response.ok) {
        setExpenses(data.expenses || []);
      } else {
        toast.error(data.error || 'Failed to load expenses');
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Error loading expenses');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  const fetchListings = useCallback(async () => {
    if (!currentUser) return;
    try {
      const userListings = await queryDocuments('listings', [
        where('propertyOwnerUid', '==', currentUser.uid)
      ]);
      setListings(userListings);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchExpenses();
      fetchListings();
    }
  }, [currentUser, fetchExpenses, fetchListings]);

  const onSubmit = async (data: ExpenseFormData) => {
    if (!currentUser) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('userId', currentUser.uid);
      formData.append('amount', data.amount.toString());
      formData.append('category', data.category);
      formData.append('dateAdded', data.dateAdded);
      if (data.listingId) {
        formData.append('listingId', data.listingId);
      }

      if (data.receipt && data.receipt.length > 0) {
        formData.append('receipt', data.receipt[0]);
      }

      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/addLandlordExpense`,
        {
          method: 'POST',
          body: formData
        }
      );

      const result = await response.json();

      if (response.ok) {
        toast.success('Expense added successfully');
        reset();
        setReceiptPreview(null);
        setShowForm(false);
        fetchExpenses(); // Refetch expenses to include the new one
      } else {
        toast.error(result.error || 'Failed to add expense');
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Error adding expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (expenseId: string) => {
    setExpenseToDelete(expenseId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!expenseToDelete) return;

    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/deleteLandlordExpense?expenseId=${expenseToDelete}`,
        {
          method: 'DELETE'
        }
      );

      if (response.ok) {
        toast.success('Expense deleted');
        setExpenses(expenses.filter((e) => e.id !== expenseToDelete));
      } else {
        toast.error('Failed to delete expense');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Error deleting expense');
    } finally {
      setIsDeleteModalOpen(false);
      setExpenseToDelete(null);
    }
  };

  const getListingTitle = (listingId: string | null) => {
    if (!listingId) return 'General Expense';
    const listing = listings.find(
      (l) => l.id.toString() === listingId || l.firestoreDocId === listingId
    );
    return listing ? listing.title : 'Unknown Property';
  };

  const monthOptions = useMemo(() => {
    const months = new Set<string>();
    expenses.forEach((expense) => {
      months.add(new Date(expense.expenseDate).toISOString().slice(0, 7)); // YYYY-MM
    });
    return Array.from(months).sort((a, b) => b.localeCompare(a)); // Sort descending
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    if (selectedMonth === 'all') {
      return expenses;
    }
    return expenses.filter(
      (expense) =>
        new Date(expense.expenseDate).toISOString().slice(0, 7) ===
        selectedMonth
    );
  }, [expenses, selectedMonth]);

  const totalExpenses = filteredExpenses.reduce(
    (sum, expense) => sum + expense.amount,
    0
  );

  const handleDownloadCSV = () => {
    const headers = ['Date', 'Category', 'Property', 'Amount', 'Receipt URL'];
    const rows = filteredExpenses.map((expense) => [
      new Date(expense.expenseDate).toLocaleDateString(),
      expense.category,
      getListingTitle(expense.listingId),
      expense.amount.toFixed(2),
      expense.receiptUrl || 'N/A'
    ]);
    const monthName =
      selectedMonth === 'all'
        ? 'all-months'
        : new Date(selectedMonth + '-02').toLocaleString('default', {
            month: 'long',
            year: 'numeric'
          });
    downloadCSV(headers, rows, `expenses-${monthName}.csv`);
    toast.success('CSV downloaded successfully');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Expense Tracker</h2>
          <p className="text-sm text-gray-500">
            Track your rental property expenses
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Total For Period</p>
          <p className="text-2xl font-bold text-blue-600">
            €{totalExpenses.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="p-6">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="mb-6 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Add New Expense
          </button>
        ) : (
          <div className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Expense</h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  reset();
                  setReceiptPreview(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    {...register('dateAdded', { required: 'Date is required' })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2"
                  />
                  {errors.dateAdded && (
                    <span className="text-red-500 text-xs">
                      {errors.dateAdded.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('amount', {
                      required: 'Amount is required',
                      min: 0
                    })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2"
                  />
                  {errors.amount && (
                    <span className="text-red-500 text-xs">
                      {errors.amount.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    {...register('category', {
                      required: 'Category is required'
                    })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2"
                  >
                    <option value="">Select Category</option>
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <span className="text-red-500 text-xs">
                      {errors.category.message}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property (Optional)
                  </label>
                  <select
                    {...register('listingId')}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2"
                  >
                    <option value="">
                      General Expense (No specific property)
                    </option>
                    {listings.map((listing) => (
                      <option key={listing.id} value={listing.id}>
                        {listing.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Receipt (Image)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  {...register('receipt')}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2"
                />
              </div>

              {receiptPreview && (
                <div className="relative w-32 h-32">
                  <img
                    src={receiptPreview}
                    alt="Receipt preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setValue('receipt', new DataTransfer().files);
                      setReceiptPreview(null);
                    }}
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save Expense'}
                </button>
              </div>
            </form>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading expenses...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <DocumentArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900">
              No expenses recorded
            </h3>
            <p className="text-gray-500 mb-4">
              Start tracking your property expenses today.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="text-blue-600 font-medium hover:text-blue-700"
            >
              Add your first expense
            </button>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <div>
                <label
                  htmlFor="month-filter"
                  className="block text-sm font-medium text-gray-700"
                >
                  Filter by Month
                </label>
                <select
                  id="month-filter"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="all">All Months</option>
                  {monthOptions.map((month) => (
                    <option key={month} value={month}>
                      {new Date(month + '-02').toLocaleString('default', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleDownloadCSV}
                className="mt-5 flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
                Download CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Receipt
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredExpenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(expense.expenseDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {expense.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getListingTitle(expense.listingId)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        €{expense.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {expense.receiptUrl ? (
                          <a
                            href={expense.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <PaperClipIcon className="w-4 h-4" />
                            View
                          </a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Expense"
        description="Are you sure you want to delete this expense? This action cannot be undone."
      />
    </div>
  );
};

export default ExpenseTracker;
